import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import Select from '../Select';

/**
 * YearSelect - A reusable year dropdown component with consistent styling
 *
 * Displays 4 year options: current year -2, -1, current, +1
 * Default value is the current year
 *
 * @param {number} value - The selected year value
 * @param {Function} onChange - Callback when year changes (receives event)
 * @param {string} width - Width of the select (default: "150px")
 * @param {string} label - Optional label for the select
 *
 * @example
 * // Basic usage
 * <YearSelect value={yearFilter} onChange={handleYearChange} />
 *
 * // With label
 * <YearSelect value={yearFilter} onChange={handleYearChange} label="Year" />
 */
const YearSelect = ({ value, onChange, width = '150px', label, ...props }) => {
  const thisYear = Number(moment().format('YYYY'));

  // Generate year options: current year -2, -1, current, +1 (ascending order)
  const yearOptions = useMemo(
    () => [
      { label: String(thisYear - 2), value: thisYear - 2 },
      { label: String(thisYear - 1), value: thisYear - 1 },
      { label: String(thisYear), value: thisYear },
      { label: String(thisYear + 1), value: thisYear + 1 },
    ],
    [thisYear]
  );

  // Default to current year if no value provided
  const selectedValue = value ?? thisYear;

  return (
    <Select
      value={selectedValue}
      onChange={onChange}
      options={yearOptions}
      width={width}
      label={label}
      {...props}
    />
  );
};

YearSelect.propTypes = {
  /** The selected year value */
  value: PropTypes.number,
  /** Callback when year changes */
  onChange: PropTypes.func.isRequired,
  /** Width of the select */
  width: PropTypes.string,
  /** Optional label for the select */
  label: PropTypes.string,
};

export default YearSelect;
